import React, { useState } from "react";
import usePageTitle from '../../hooks/usePageTitle';
import { usePartner } from '../../context/PartnerContext';
import { Link, useNavigate } from "react-router-dom";
import api from "../../api/api";

const LoginPage = () => {
  usePageTitle("Sign In");
  const { appName, logoUrl, partner, loading: brandLoading, tagline } = usePartner();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notVerified, setNotVerified] = useState(false);
  const [resendStatus, setResendStatus] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setNotVerified(false);
    setResendStatus("");
    try {
      const data = await api.login(email, password);
      const role = (data.user?.primary_role || data.primary_role || "trader").toLowerCase();
      const isAdminFlag = !!(data.user?.is_admin || data.is_admin);
      localStorage.setItem("is_admin", isAdminFlag ? "true" : "false");
      localStorage.setItem("user_role", role);
      localStorage.setItem("user_email", email);
      if (role === "admin") navigate("/admin");
      else if (role === "investor") navigate("/investor");
      else if (role === "analyst") navigate("/analyst");
      else navigate("/trader");
    } catch (err) {
      const msg = err.message || "Login failed";
      if (msg.toLowerCase().includes("not verified")) {
        setNotVerified(true);
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setResendStatus("sending");
    try {
      const res = await fetch("/api/auth/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setResendStatus(res.ok ? "sent" : "error");
    } catch {
      setResendStatus("error");
    }
  };

  return (
    <div className="auth-shell-split">
      <div className="auth-visual">
        <div className="auth-visual-tagline">Trust the data.<br />Then follow.</div>
      </div>
      <div className="auth-form-side">
      <div className="auth-panel">
        <div className="auth-brand">
          <div className="auth-brand-logo" style={{display:"flex",alignItems:"center",gap:8,justifyContent:"center"}}>{(logoUrl || (!partner && !brandLoading)) && <img src={logoUrl || "/logo.jpeg"} alt={appName || "CrunchAlpha"} style={{height:32,width:32,borderRadius:6,objectFit:"cover"}} onError={e=>e.target.style.display="none"}/>} {appName || "CrunchAlpha"}</div>
          {tagline && <div className="auth-brand-tagline">{tagline}</div>}
        </div>
        <div className="auth-card">
          <h2>Sign in</h2>
          <p className="auth-subtitle">Welcome back. Enter your credentials to continue.</p>
          {error && <div className="auth-error">{error}</div>}
          {notVerified && (
            <div className="auth-warn">
              <p style={{ margin:"0 0 8px", fontWeight:500 }}>Email not verified</p>
              <p style={{ margin:0, fontSize:"12px", color:"var(--text-muted)", lineHeight:1.5 }}>
                Please verify your email before signing in.
              </p>
              {resendStatus === "" && (
                <button onClick={handleResend}
                  style={{ marginTop:"10px", background:"none", border:"none",
                    color:"var(--accent)", cursor:"pointer", padding:0,
                    fontSize:"13px", fontWeight:500 }}>
                  Resend verification email
                </button>
              )}
              {resendStatus === "sending" && (
                <p style={{ margin:"8px 0 0", fontSize:"13px", color:"var(--text-faint)" }}>Sending...</p>
              )}
              {resendStatus === "sent" && (
                <p style={{ margin:"8px 0 0", fontSize:"13px", color:"var(--success)" }}>
                  Verification email sent. Check your inbox.
                </p>
              )}
              {resendStatus === "error" && (
                <p style={{ margin:"8px 0 0", fontSize:"13px", color:"var(--danger)" }}>
                  Failed to send. Please try again later.
                </p>
              )}
            </div>
          )}
          <form onSubmit={onSubmit} className="auth-form">
            <label>
              <span>Email address</span>
              <input type="email" required autoComplete="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            </label>
            <label>
              <span>Password</span>
              <div style={{ position:"relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  required autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  style={{ paddingRight:"44px" }}
                />
                <button type="button" tabIndex={-1}
                  onClick={() => setShowPassword(v => !v)}
                  style={{ position:"absolute", right:"12px", top:"50%", transform:"translateY(-50%)",
                    background:"none", border:"none", cursor:"pointer", color:"var(--text-faint)",
                    padding:"2px", display:"flex", alignItems:"center" }}>
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
                      <path d="M3 3l14 14M10 4C5.6 4 2 10 2 10s1.4 2.2 3.8 4M10 16c4.4 0 8-6 8-6s-1.4-2.2-3.8-4"/><circle cx="10" cy="10" r="2.5"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
                      <path d="M2 10s3.6-6 8-6 8 6 8 6-3.6 6-8 6-8-6-8-6z"/><circle cx="10" cy="10" r="2.5"/>
                    </svg>
                  )}
                </button>
              </div>
            </label>
            <div style={{ textAlign:"right", marginTop:"-4px" }}>
              <Link to="/forgot-password" style={{ fontSize:"12px", color:"var(--text-faint)" }}>
                Forgot password?
              </Link>
            </div>
            <button type="submit" className="auth-button" disabled={busy}>
              {busy ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>
        <p className="auth-footer">
          Don&apos;t have an account? <Link to="/register">Create account</Link>
        </p>
      </div>
      </div>
    </div>
  );
};

export default LoginPage;
